import React from "react";
import { Box, Typography, Stack, Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import {
  getPedidoProfitMeta,
  formatPedidoAmount,
  formatProfitPct,
  PROFIT_LEGEND,
  getPedidoDocKindMeta,
  getPedidoTipocontMeta,
  getPedidoPendingAmount,
  getPedidoDisplayInvSales,
  rowMatchesPedidoFocus,
} from "./pedidosVentaUtils";
import { pageMobileCardSx } from "../../styles/pageModernStyles";
import ClienteAvatar, { RepAvatar } from "./ClienteAvatar";

const PROFIT_LABEL = Object.fromEntries(
  PROFIT_LEGEND.map(({ key, label }) => [key, label]),
);

function UnitTile({ theme, label, value, highlight, tone = "default" }) {
  const tones = {
    default: {
      bg: alpha(theme.palette.text.primary, 0.04),
      color: theme.palette.text.primary,
    },
    danger: {
      bg: alpha(theme.palette.error.main, 0.1),
      color: theme.palette.error.main,
    },
    muted: {
      bg: alpha(theme.palette.text.primary, 0.03),
      color: theme.palette.text.disabled,
    },
  };
  const t = tones[tone] || tones.default;
  const num = Number(value);
  const isEmpty = value === undefined || value === null || value === "";

  return (
    <Box
      sx={{
        textAlign: "center",
        py: 0.85,
        px: 0.5,
        borderRadius: 1.75,
        bgcolor: t.bg,
        border: "1px solid",
        borderColor: highlight
          ? alpha(theme.palette.error.main, 0.22)
          : alpha(theme.palette.divider, 0.55),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontWeight: 700,
          fontSize: "0.62rem",
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: "text.secondary",
          lineHeight: 1.1,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.35,
          fontWeight: highlight ? 800 : 700,
          fontSize: highlight ? "0.95rem" : "0.88rem",
          fontVariantNumeric: "tabular-nums",
          color: isEmpty ? "text.disabled" : t.color,
          lineHeight: 1.1,
        }}
      >
        {isEmpty ? "—" : value}
      </Typography>
    </Box>
  );
}

function MoneyCell({ label, value, theme }) {
  const num = Number(value);
  const isNeg = !Number.isNaN(num) && num < 0;
  const isEmpty = value === undefined || value === null || value === "";

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, fontSize: "0.65rem", letterSpacing: 0.3 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.8rem",
          fontVariantNumeric: "tabular-nums",
          color: isEmpty
            ? "text.disabled"
            : isNeg
              ? theme.palette.error.main
              : "text.primary",
        }}
      >
        {isEmpty ? "—" : formatPedidoAmount(value)}
      </Typography>
    </Box>
  );
}

function DatePill({ icon: Icon, label, theme }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.85,
        py: 0.35,
        borderRadius: 99,
        bgcolor: alpha(theme.palette.primary.main, 0.07),
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, 0.14),
      }}
    >
      <Icon sx={{ fontSize: 13, color: "primary.main", opacity: 0.85 }} />
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, fontSize: "0.68rem", color: "text.secondary" }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const PedidosVentaMobile = ({
  theme,
  rows,
  focus = null,
  onFocusRow,
  onOpenDoc,
}) => {
  const accent = theme.palette.primary.main;
  const isDark = theme.palette.mode === "dark";

  if (!rows.length) {
    return (
      <Box
        sx={{
          py: 5,
          px: 2,
          textAlign: "center",
          borderRadius: 3,
          border: "1px dashed",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.background.paper, 0.9),
        }}
      >
        <Typography color="text.secondary" fontWeight={600}>
          No hay pedidos con el filtro actual
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.25}>
      {rows.map((row, index) => {
        const profit = getPedidoProfitMeta(row);
        const docKind = getPedidoDocKindMeta(row);
        const tipocontMeta = getPedidoTipocontMeta(row);
        const pending = Number(row.UN_Ptes || 0);
        const cancelled = Number(row.UN_Anul || 0);
        const highlighted = rowMatchesPedidoFocus(row, focus);
        const dimmed = Boolean(focus) && !highlighted;
        const isParte = tipocontMeta.isParte;
        const profitAccent =
          docKind.kind === "invoices"
            ? docKind.color
            : profit.type === "noCosts"
              ? theme.palette.secondary.main
              : docKind.color;
        const productLine = [row.CODART, row.ARTALIAS, row.CAR1]
          .filter(Boolean)
          .join(" · ");

        return (
          <Box
            key={`${row.PorchDoc || "row"}-${row.CODART || ""}-${row.TIPOCONT || ""}-${index}`}
            onClick={() => onOpenDoc?.(row)}
            sx={{
              ...pageMobileCardSx(theme, {
                accentColor: profitAccent,
                active: highlighted,
              }),
              pl: 2,
              pr: 1.5,
              pt: 1.35,
              pb: 0,
              overflow: "hidden",
              cursor: "pointer",
              opacity: dimmed ? 0.45 : isParte ? 0.78 : 1,
              borderLeft: isParte
                ? `3px solid ${alpha(tipocontMeta.color, 0.7)}`
                : undefined,
            }}
          >
            {/* Cabecera */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={1}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    onClick={(event) => event.stopPropagation()}
                    sx={{ pt: 0.2, flexShrink: 0 }}
                  >
                    <ClienteAvatar
                      row={row}
                      onPaintCliente={(item) => onFocusRow?.("cliente", item)}
                    />
                    <RepAvatar row={row} />
                  </Stack>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        color: "text.secondary",
                        fontSize: "0.62rem",
                      }}
                    >
                      Cliente
                    </Typography>
                    <Typography
                      fontWeight={800}
                      sx={{
                        fontSize: "0.9rem",
                        lineHeight: 1.25,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {row.CLIPRO}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      useFlexGap
                      flexWrap="wrap"
                      sx={{ mt: 0.6 }}
                    >
                      {row.PorchDoc ? (
                        <Chip
                          size="small"
                          label={row.PorchDoc}
                          sx={{
                            height: 22,
                            fontWeight: 800,
                            fontSize: "0.68rem",
                            fontFamily: "ui-monospace, monospace",
                            bgcolor: alpha(theme.palette.text.primary, 0.06),
                            border: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.8),
                          }}
                        />
                      ) : null}
                      <Chip
                        size="small"
                        label={tipocontMeta.label}
                        sx={{
                          height: 22,
                          fontWeight: 800,
                          fontSize: "0.66rem",
                          color: tipocontMeta.color,
                          bgcolor: alpha(tipocontMeta.color, 0.12),
                          border: "1px solid",
                          borderColor: alpha(tipocontMeta.color, 0.35),
                        }}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                <Chip
                  size="small"
                  label={docKind.label}
                  sx={{
                    height: 22,
                    fontWeight: 800,
                    fontSize: "0.68rem",
                    color: docKind.color,
                    bgcolor: alpha(docKind.color, 0.14),
                    border: "1px solid",
                    borderColor: alpha(docKind.color, 0.35),
                  }}
                />
                {profit.type === "noCosts" ||
                profit.type === "articulo" ||
                profit.type === "directoInv" ? (
                  <Chip
                    size="small"
                    label={
                      profit.type === "articulo"
                        ? "Coste ficha"
                        : profit.type === "directoInv"
                          ? "Profit = fact. Directo"
                          : "Sin compra"
                    }
                    sx={{
                      height: 22,
                      fontWeight: 800,
                      fontSize: "0.66rem",
                      color: profit.color,
                      bgcolor: alpha(profit.color, 0.14),
                      border: "1px solid",
                      borderColor: alpha(profit.color, 0.35),
                    }}
                  />
                ) : null}
                {row.SITUACION ? (
                  <Chip
                    size="small"
                    label={row.SITUACION}
                    sx={{
                      height: 22,
                      minWidth: 28,
                      fontWeight: 800,
                      fontSize: "0.72rem",
                      bgcolor:
                        String(row.SITUACION).toUpperCase() === "S"
                          ? alpha(theme.palette.success.main, 0.14)
                          : alpha(theme.palette.warning.main, 0.14),
                      color:
                        String(row.SITUACION).toUpperCase() === "S"
                          ? theme.palette.success.dark
                          : theme.palette.warning.dark,
                      border: "1px solid",
                      borderColor:
                        String(row.SITUACION).toUpperCase() === "S"
                          ? alpha(theme.palette.success.main, 0.35)
                          : alpha(theme.palette.warning.main, 0.35),
                    }}
                  />
                ) : null}
              </Stack>
            </Stack>

            {/* Artículo */}
            {productLine ? (
              <Box
                sx={{
                  mt: 1.1,
                  p: 1,
                  borderRadius: 2,
                  bgcolor: alpha(accent, isDark ? 0.12 : 0.06),
                  border: "1px solid",
                  borderColor: alpha(accent, 0.12),
                }}
              >
                <Stack direction="row" spacing={0.75} alignItems="flex-start">
                  <Inventory2OutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: "primary.main",
                      mt: 0.15,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: "text.secondary" }}
                    >
                      Artículo
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        lineHeight: 1.3,
                        wordBreak: "break-word",
                      }}
                    >
                      {productLine}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ) : null}

            {/* Fechas */}
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ mt: 1 }}
              flexWrap="wrap"
              useFlexGap
            >
              {row.FECHA ? (
                <DatePill
                  theme={theme}
                  icon={CalendarTodayOutlinedIcon}
                  label={`PO ${dayjs(row.FECHA).format("DD/MM/YY")}`}
                />
              ) : null}
              {row.DeliveryDate ? (
                <DatePill
                  theme={theme}
                  icon={LocalShippingOutlinedIcon}
                  label={`Entrega ${dayjs(row.DeliveryDate).format("DD/MM/YY")}`}
                />
              ) : null}
              {row.CODMON ? (
                <Chip
                  size="small"
                  label={row.CODMON}
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    fontSize: "0.68rem",
                    bgcolor: alpha(theme.palette.text.primary, 0.06),
                  }}
                />
              ) : null}
            </Stack>

            {/* Unidades */}
            <Box
              sx={{
                mt: 1.15,
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                gap: 0.75,
              }}
            >
              <UnitTile theme={theme} label="Uds." value={row.VentasUN} />
              <UnitTile theme={theme} label="Serv." value={row.UN_Serv} />
              <UnitTile
                theme={theme}
                label="Anul."
                value={row.UN_Anul}
                highlight={cancelled > 0}
                tone={cancelled > 0 ? "danger" : "muted"}
              />
              <UnitTile
                theme={theme}
                label="Ptes."
                value={row.UN_Ptes}
                highlight={pending > 0}
                tone={pending > 0 ? "danger" : "muted"}
              />
            </Box>
            {pending > 0 ? (
              <Typography
                sx={{
                  mt: 0.6,
                  fontWeight: 800,
                  fontSize: "0.78rem",
                  color: "error.dark",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                Pendiente{" "}
                {formatPedidoAmount(getPedidoPendingAmount(row)) || "0,00"} €
              </Typography>
            ) : null}

            {/* Importes */}
            <Box
              sx={{
                mt: 0.85,
                p: 1,
                borderRadius: 2,
                bgcolor: alpha(
                  theme.palette.background.default,
                  isDark ? 0.35 : 0.55,
                ),
                border: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.65),
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1,
                rowGap: 0.85,
              }}
            >
              <MoneyCell
                theme={theme}
                label="Compra €"
                value={row.PedidoCompraE}
              />
              <MoneyCell
                theme={theme}
                label="Venta €"
                value={row.VentaBaseEUR}
              />
              <MoneyCell
                theme={theme}
                label="Fact. compra"
                value={row.InvCostMoneda}
              />
              <MoneyCell
                theme={theme}
                label="Fact. venta"
                value={getPedidoDisplayInvSales(row)}
              />
            </Box>

            {/* Profit */}
            <Box
              sx={{
                mt: 1.15,
                mx: -2,
                mb: 0,
                px: 1.5,
                py: 1.1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                background: `linear-gradient(135deg, ${alpha(profitAccent, isDark ? 0.28 : 0.16)} 0%, ${alpha(profitAccent, isDark ? 0.14 : 0.08)} 100%)`,
                borderTop: "1px solid",
                borderColor: alpha(profitAccent, 0.22),
              }}
            >
              <Box>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      color: profitAccent,
                    }}
                  >
                    Profit
                  </Typography>
                  {profit.type !== "none" ? (
                    <Chip
                      size="small"
                      label={PROFIT_LABEL[profit.type] || profit.type}
                      sx={{
                        height: 18,
                        fontSize: "0.6rem",
                        fontWeight: 800,
                        bgcolor: alpha(profitAccent, 0.18),
                        color: profitAccent,
                      }}
                    />
                  ) : null}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                  >
                    · {tipocontMeta.short}
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: "1.15rem",
                    lineHeight: 1.1,
                    color: profit.color || profitAccent,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatPedidoAmount(profit.profitAbs, { decimals: 2 }) ||
                    "0,00"}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: "1.35rem",
                  lineHeight: 1,
                  color: profit.color || profitAccent,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatProfitPct(profit.profitPct)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default PedidosVentaMobile;
